import DOMPurify from 'dompurify';

// Mirrors the widget's sanitize.helper, plus mark/small/sub/sup which the backend emits in markdown table cells.
const ALLOWED_TAGS = [
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'br',
  'p',
  'div',
  'span',
  'ul',
  'ol',
  'li',
  'blockquote',
  'hr',
  'code',
  'pre',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'a',
  'img',
  'mark',
  'small',
  'sub',
  'sup',
];

// style/src are load-bearing: the Inbox bubble has no typography plugin, so backend inline styles size images and align table cells.
const ALLOWED_ATTR = [
  'href',
  'title',
  'target',
  'rel',
  'class',
  'id',
  'aria-label',
  'aria-hidden',
  'role',
  'colspan',
  'rowspan',
  'align',
  'style',
  'src',
  'alt',
  'width',
  'height',
  'loading',
];

// Visitor content must never render a clickable link or inline image to an operator, so drop a/img entirely as a backstop to the unwrap below.
const VISITOR_ALLOWED_TAGS = ALLOWED_TAGS.filter((tag) => tag !== 'a' && tag !== 'img');

// Visitor content is attacker-influenced (esp. via translation), so also deny style/class: no CSS overlays and no reuse of app classes to obscure or intercept the Inbox.
const VISITOR_ALLOWED_ATTR = ALLOWED_ATTR.filter((attr) => attr !== 'style' && attr !== 'class');

export const sanitizeWithExternalLinks = (html: string): string => {
  // Not redundant with the backend's rel/target: markdown.markdown on the translation path emits a bare <a href> that would navigate the operator's tab.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  try {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
};

// Visitor-authored text, including the no-author-filter translation path: unwrap a/img to their bare URL as text (mirroring content_inbox) so a visitor cannot surface a clickable link or inline image to an operator.
export const sanitizeVisitorHtml = (html: string): string => {
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('a, img').forEach((element) => {
    // A nested <a><img></a> detaches the inner node when the outer is replaced; skip it.
    if (!element.parentNode) return;
    const url = element.getAttribute('href') || element.getAttribute('src') || '';
    element.replaceWith(document.createTextNode(url || element.textContent || ''));
  });

  return DOMPurify.sanitize(template.innerHTML, {
    ALLOWED_TAGS: VISITOR_ALLOWED_TAGS,
    ALLOWED_ATTR: VISITOR_ALLOWED_ATTR,
  });
};
